import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";
import { STRATEGIES } from "@/data/strategies";

const StrategiesPage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.strategiesPage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Strategies"
          label="Four mandates · One underwriting standard"
          title={
            <>
              The investment{" "}
              <span className="italic text-[var(--kr-gold)]">framework</span>.
            </>
          }
          description="A disciplined platform organised around four mandates, supported by a vehicle architecture calibrated to the cash-flow profile, duration and governance preferences of sophisticated institutional capital."
          breadcrumb="Strategies"
        />

        <section className="py-24 lg:py-32">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 space-y-24 lg:space-y-32">
            {STRATEGIES.map((m) => (
              <article
                key={m.n}
                id={m.slug}
                data-testid={`${LANDING.strategiesPageCardPrefix}${m.n}`}
                className="grid lg:grid-cols-12 gap-12 lg:gap-16 pb-16 border-b border-[var(--kr-line-soft)] last:border-b-0 scroll-mt-32"
              >
                <div className="lg:col-span-5">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                      Mandate {m.n}
                    </span>
                    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
                  </div>
                  <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.9rem,3.4vw,2.7rem)] leading-[1.1] mb-6">
                    {m.title}
                  </h2>
                  <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.85] max-w-[52ch] mb-8">
                    {m.summary}
                  </p>
                  <Link
                    to={`/strategies/${m.slug}`}
                    className="kr-link font-mono-label text-[11px]"
                  >
                    Read the full mandate →
                  </Link>
                </div>

                <div className="lg:col-span-7 grid md:grid-cols-2 gap-10">
                  <div>
                    <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-5">
                      Investment Approach
                    </div>
                    <ul className="space-y-4">
                      {m.approach.slice(0, 4).map((d) => (
                        <li
                          key={d}
                          className="flex items-start gap-3 text-[var(--kr-text)] text-[14.5px] leading-[1.7]"
                        >
                          <span
                            aria-hidden
                            className="mt-[8px] block w-[5px] h-[5px] flex-shrink-0"
                            style={{
                              background: "var(--kr-gold)",
                              transform: "rotate(45deg)",
                            }}
                          />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="font-mono-label text-[10px] text-[var(--kr-text-muted)] mb-5">
                      Intended Vehicles
                    </div>
                    <ul className="space-y-3">
                      {m.vehicles.map((v) => (
                        <li
                          key={v}
                          className="font-serif-display text-[1.1rem] text-[var(--kr-text)] border-b border-[var(--kr-line)] pb-3"
                        >
                          {v}
                          <span className="ml-2 text-[10px] font-mono-label text-[var(--kr-text-dim)] uppercase tracking-[0.18em]">
                            · Working title
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Bridge: when the fit is partial or external */}
        <section
          data-testid={LANDING.strategiesPagePathwaysBridge}
          className="border-t border-[var(--kr-line-soft)] py-20 lg:py-24"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  When the fit is partial
                </span>
                <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.6rem,2.8vw,2.2rem)] leading-[1.18] max-w-[26ch]">
                Beyond the four mandates —{" "}
                <span className="italic text-[var(--kr-gold)]">
                  Selective Capital Pathways
                </span>
                .
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pl-8">
              <p className="text-[var(--kr-text-muted)] text-[16px] leading-[1.85] max-w-[68ch] mb-8">
                Where a transaction sits outside the platform&apos;s direct
                mandate parameters, the partnership operates four documented
                capital pathways — including coordinated introductions to
                selected equity, private credit, real estate debt, and
                strategic capital partners within our external institutional
                network.
              </p>
              <Link
                to="/capital-pathways"
                className="kr-link font-mono-label text-[11px]"
              >
                Read the Pathways Framework →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA strip */}
        <section className="border-t border-[var(--kr-line)] py-24 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Investor Access
                </span>
                <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15] max-w-[26ch]">
                Detailed mandate documentation is reserved for{" "}
                <span className="italic text-[var(--kr-gold)]">
                  qualified institutional
                </span>{" "}
                counterparties.
              </h2>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.strategiesPageRequest}
                className="kr-btn-primary"
              >
                Request Investor Access
                <span aria-hidden>→</span>
              </button>
              <Link to="/contact" className="kr-btn-ghost">
                General Enquiry
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal open={modalOpen} onClose={closeModal} />
    </>
  );
};

export default StrategiesPage;
